import { forwardRef, useEffect, useMemo } from "react";
import InfiniteScroll, {
  type Props as InfiniteScrollProps,
} from "react-infinite-scroll-component";

import LoadingAnimation from "@/components/loading-animation";
import { MOBILE_MAP_HEIGHT, MOBILE_MAP_HEIGHT_IOS } from "@/helpers/constants";

import BookingFilter from "../components/booking-filter";
import ScaryFilter from "../components/scary-filter";
import ChristmasFilter from "../filters/christmas-filter";
import type {
  GameOrLocationType,
  LocationType,
  SearchGameType,
  SortEnum,
} from "../types";
import CategoryFilter from "./category-filter";
import Games from "./games";
import Locations from "./locations";
import NoResults from "./no-results";

interface ListProps extends Omit<InfiniteScrollProps, "children"> {
  hasClosedGames?: boolean;
  isIos: boolean;
  loading: boolean;
  objects: GameOrLocationType[];
  onHover(locationId: string | null): void;
  onShowClosedGames?: () => void;
  sortBy: SortEnum;
}

export type ListRef = HTMLDivElement;

export default forwardRef<ListRef, ListProps>(function List(
  {
    hasClosedGames,
    isIos,
    objects,
    onHover,
    onShowClosedGames,
    sortBy,
    loading,
    ...props
  },
  ref
) {
  useEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    (window as any).TS = {
      token: process.env.TOPSORT_TOKEN,
      url: process.env.TOPSORT_URL,
    };

    // biome-ignore lint/suspicious/noExplicitAny: legacy
    import("@topsort/analytics.js" as any);
  }, []);

  const objectType = useMemo(() => {
    if (!objects.length) {
      return "none";
    }
    return objects[0].__typename;
  }, [objects]);

  return (
    <div
      className="relative z-1 mt-(--map-height) bg-[url(/images/body.png)] bg-light-black bg-size-[300%] bg-top bg-no-repeat md:mt-0 md:bg-transparent md:pt-43.5"
      ref={ref}
      style={
        {
          "--map-height": isIos ? MOBILE_MAP_HEIGHT_IOS : MOBILE_MAP_HEIGHT,
        } as React.CSSProperties
      }
    >
      <div className="md:hidden">
        <CategoryFilter />
        <div className="mt-4 flex gap-2 px-4">
          <ChristmasFilter className="w-auto max-w-[185px] grow-1" />
          <ScaryFilter />
          <BookingFilter />
        </div>
      </div>
      {loading ? (
        <div className="h-screen">
          <LoadingAnimation />
        </div>
      ) : (
        <InfiniteScroll {...props} loader={<LoadingAnimation />}>
          <div className="px-4 md:px-0">
            {(() => {
              if (objectType === "none") {
                return (
                  <NoResults
                    hasClosedGames={hasClosedGames}
                    onShowClosedGames={onShowClosedGames}
                  />
                );
              }
              if (objectType === "GameType") {
                return (
                  <Games
                    className="mt-[21px] md:mt-2.5"
                    games={objects as SearchGameType[]}
                    onHover={onHover}
                  />
                );
              }
              return (
                <Locations
                  locations={objects as LocationType[]}
                  onHover={onHover}
                />
              );
            })()}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
});
